import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { z } from "zod";

import { db } from "integrations/firebase/firestore";

import { resolveWallTime, toDate } from "../event-experience/agenda-schedule";

import {
  type AnnouncementDeletion,
  deletionFieldsSchema,
  deletionOf,
  mayDelete,
} from "./announcement-deletion";
import { clockName, COMPLETE, describeWall } from "./WallTimeField";

/**
 * Announcements booked for later, as the organiser sees them.
 *
 * Read-only here. The rows are written by SCHEDULE_EVENT_ANNOUNCEMENT,
 * UPDATE_SCHEDULED_ANNOUNCEMENT and CANCEL_SCHEDULED_ANNOUNCEMENT through
 * their trigger, and by the scheduler that sends them
 * (functions/python/app/application/service/scheduled_announcement.py).
 */

export const SCHEDULED_STATUSES = [
  "SCHEDULED",
  "SENDING",
  "SENT",
  "CANCELLED",
  "FAILED",
] as const;
/**
 * A row can also be a SCHEDULE_EVENT_ANNOUNCEMENT command no trigger has
 * applied yet. It is shown so an organiser who reloads while the command is
 * pending sees the booking coming and does not file it a second time; the
 * scheduler re-applies such a command about 90 seconds after it was filed.
 */
export type ScheduledStatus = (typeof SCHEDULED_STATUSES)[number] | "PENDING";

export const scheduledAnnouncementSchema = deletionFieldsSchema.extend({
  title: z.string(),
  body: z.string(),
  sendAt: z.any(),
  status: z.enum(SCHEDULED_STATUSES),
  error: z.string().optional(),
  recipientCount: z.number().optional(),
  // The SEND_EVENT_ANNOUNCEMENT the scheduler filed for it, once claimed.
  sendCommandId: z.string().optional(),
  // A feedback form of this event the notification links to.
  feedbackFormId: z.string().optional(),
  // The dinner seating {dinner_table} is filled from (TNG-94).
  seatingId: z.string().optional(),
});

export type ScheduledAnnouncement = {
  id: string;
  title: string;
  body: string;
  sendAt: Date | null;
  status: ScheduledStatus;
  error?: string;
  recipientCount?: number;
  sendCommandId?: string;
  feedbackFormId?: string;
  seatingId?: string;
  deletion?: AnnouncementDeletion;
};

/**
 * The send command "Delete for everyone" names for a booked announcement, or
 * null while it cannot be deleted for everyone.
 *
 * Only once it may have reached somebody: sent, going out, or failed after
 * the scheduler filed its send. One that failed before that (the event was
 * not published) reached nobody.
 */
export function deletableSendCommandId(
  item: ScheduledAnnouncement,
): string | null {
  if (!mayDelete(item.deletion)) return null;
  if (item.status === "SENT" || item.status === "SENDING")
    return item.sendCommandId ?? `scheduled-${item.id}`;
  if (item.status === "FAILED" && item.sendCommandId) return item.sendCommandId;
  return null;
}

/**
 * The same bound firestore.rules puts on sendAt, so a time too far out is a
 * sentence here rather than a permission error that names no field.
 */
export const MAX_AHEAD_DAYS = 366;

/** How the scheduler's one-minute tick reads to an organiser. */
export const SEND_PRECISION = "It goes out within about a minute of that time.";

/**
 * A send time typed on the event's clock as the instant it names, or what is
 * wrong with it.
 *
 * A time the clocks skip for daylight saving names no instant, and saying so
 * beats sending an hour off without a word. A time in the past is refused
 * here as the rules would refuse it, against the same `now`.
 */
export function parseSendTime(
  wall: string,
  timeZone: string | undefined,
  now: Date,
): { instant: Date } | { problem: string } {
  const missing = "Choose a date and a time to send it.";
  if (!COMPLETE.test(wall)) return { problem: missing };
  const resolved = resolveWallTime(wall, timeZone);
  if (resolved.kind === "skipped")
    return {
      problem: `${describeWall(wall)} does not exist in ${clockName(timeZone)}, because the clocks go forward that night. Choose a time after the change.`,
    };
  if (!("instant" in resolved)) return { problem: missing };
  if (resolved.instant.getTime() <= now.getTime())
    return {
      problem: "That time has already passed. Choose a time in the future.",
    };
  if (resolved.instant.getTime() > now.getTime() + MAX_AHEAD_DAYS * 86_400_000)
    return { problem: `Choose a time within ${MAX_AHEAD_DAYS} days.` };
  return { instant: resolved.instant };
}

/** "Thu 24 Sep 2026, 14:00 Berlin time" on the event's clock. */
export function formatSendTime(
  at: Date | null,
  timeZone: string | undefined,
): string {
  if (!at) return "No send time";
  const text = new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  }).format(at);
  return `${text} ${clockName(timeZone)}`;
}

/**
 * Upcoming first, soonest at the top, then everything settled, newest first.
 * What the organiser can still change is what they came to the list for.
 */
export function orderScheduled(
  items: ScheduledAnnouncement[],
): ScheduledAnnouncement[] {
  const time = (item: ScheduledAnnouncement) => item.sendAt?.getTime() ?? 0;
  const open = (item: ScheduledAnnouncement) =>
    item.status === "PENDING" ||
    item.status === "SCHEDULED" ||
    item.status === "SENDING";
  return [
    ...items.filter(open).sort((a, b) => time(a) - time(b)),
    ...items.filter((item) => !open(item)).sort((a, b) => time(b) - time(a)),
  ];
}

const LIST_LIMIT = 50;

const pendingScheduleSchema = z.object({
  type: z.literal("SCHEDULE_EVENT_ANNOUNCEMENT"),
  status: z.literal("REQUESTED"),
  payload: z.object({
    title: z.string(),
    body: z.string(),
    sendAt: z.any(),
    feedbackFormId: z.string().optional(),
    seatingId: z.string().optional(),
  }),
});

/**
 * Stored rows plus pending schedule commands, without showing one booking
 * twice. A booking takes its command's id, so a command whose row already
 * exists has been applied and only the row is shown.
 */
export function withPending(
  stored: ScheduledAnnouncement[],
  pending: ScheduledAnnouncement[],
): ScheduledAnnouncement[] {
  const ids = new Set(stored.map((item) => item.id));
  return orderScheduled([
    ...stored,
    ...pending.filter((item) => !ids.has(item.id)),
  ]);
}

export function useScheduledAnnouncements(eventId: string): {
  items: ScheduledAnnouncement[];
  error: string | null;
} {
  const [stored, setStored] = useState<ScheduledAnnouncement[]>([]);
  const [pending, setPending] = useState<ScheduledAnnouncement[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return onSnapshot(
      query(
        collection(db, "events", eventId, "admin_commands"),
        where("type", "==", "SCHEDULE_EVENT_ANNOUNCEMENT"),
        where("status", "==", "REQUESTED"),
        limit(LIST_LIMIT),
      ),
      (snapshot) => {
        const parsed: ScheduledAnnouncement[] = [];
        for (const doc of snapshot.docs) {
          const result = pendingScheduleSchema.safeParse(doc.data());
          if (!result.success) continue;
          parsed.push({
            id: doc.id,
            title: result.data.payload.title,
            body: result.data.payload.body,
            sendAt: toDate(result.data.payload.sendAt),
            status: "PENDING",
            feedbackFormId: result.data.payload.feedbackFormId,
            seatingId: result.data.payload.seatingId,
          });
        }
        setPending(parsed);
      },
      // The pending rows are a courtesy; the stored list is what matters,
      // and its own listener reports its errors.
      () => setPending([]),
    );
  }, [eventId]);

  useEffect(() => {
    return onSnapshot(
      query(
        collection(db, "events", eventId, "scheduled_announcements"),
        orderBy("sendAt", "desc"),
        limit(LIST_LIMIT),
      ),
      (snapshot) => {
        const parsed: ScheduledAnnouncement[] = [];
        for (const doc of snapshot.docs) {
          const result = scheduledAnnouncementSchema.safeParse(doc.data());
          if (!result.success) continue;
          const d = result.data;
          parsed.push({
            id: doc.id,
            title: d.title,
            body: d.body,
            sendAt: toDate(d.sendAt),
            status: d.status,
            error: d.error,
            recipientCount: d.recipientCount,
            sendCommandId: d.sendCommandId,
            feedbackFormId: d.feedbackFormId,
            seatingId: d.seatingId,
            deletion: deletionOf(d),
          });
        }
        setStored(parsed);
        setError(null);
      },
      (err) => setError(err.message),
    );
  }, [eventId]);

  return { items: withPending(stored, pending), error };
}
